import React, { useEffect, useRef } from 'react';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/theme/neat.css';
import 'codemirror/mode/xml/xml.js';
import 'codemirror/mode/javascript/javascript.js';
import { Controlled as CodeMirror } from 'react-codemirror2';

import styles from './index.module.scss';

// The slice of the CodeMirror 5 instance callers need to insert at the cursor.
export interface CodemirrorEditorInstance {
  replaceSelection(text: string): void;
  focus(): void;
  refresh(): void;
}

// `mode` accepts a CodeMirror mode string ('xml', 'application/json', ...) or a
// full mode spec object. Defaults to 'xml' for MJML/HTML source editing.
export default function CodemirrorEditor(props: {
  value: string;
  onChange(val: string): void;
  refreshKey?: string | number;
  mode?: string | Record<string, unknown>;
  onEditorMount?(editor: CodemirrorEditorInstance): void;
}) {
  const { value, onChange, refreshKey, mode = 'xml', onEditorMount } = props;
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const refreshEditor = () => {
    if (!editorRef.current) return;
    editorRef.current.refresh();
  };

  useEffect(() => {
    if (!editorRef.current) return;
    const timers = [0, 80, 180].map((delay) => window.setTimeout(() => {
      refreshEditor();
    }, delay));

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [refreshKey]);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      refreshEditor();
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className={styles.container}>
      <CodeMirror
        className={styles.container}
        value={value}
        onBeforeChange={(editor, data, value) => onChange(value)}
        editorDidMount={(editor) => {
          editorRef.current = editor;
          onEditorMount?.(editor);
          const timers = [0, 80, 180].map((delay) => window.setTimeout(() => editor.refresh(), delay));
          window.setTimeout(() => {
            timers.forEach(window.clearTimeout);
          }, 220);
        }}
        options={{
          mode,
          theme: 'material',
          lineNumbers: true,
          autofocus: false,
          styleActiveLine: true,
          smartIndent: true,
          lineWrapping: true,
          foldGutter: true,
        }}
      />
    </div>
  );
}
